// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { connect } from 'react-redux';
import { Source, Layer } from 'react-map-gl';
import PropTypes from 'prop-types';
import { getFadedRasterStems } from '../footprintSelectors';
import { PROJECT_DETAILS_EXTENTS_ENABLED } from '../MapFileFootprints/extentsConfig';

// Stable empty result for surfaces where the selection fade is disabled, so
// the connect equality check never sees a fresh array.
const NO_FADED_STEMS: string[] = [];

// Unfocused tifs dim to this while another tif is selected/hovered (D46);
// mapbox's built-in raster-opacity transition makes the change a smooth fade.
const FADED_RASTER_OPACITY = 0.35;

interface RasterLayerProps {
  fileName: string;
  rasterTileJson: any;
  orgId: string;
  projectId: string;
  visible: boolean;
  faded: boolean;
}

const RasterLayer: React.FC<RasterLayerProps> = ({ fileName, rasterTileJson, orgId, projectId, visible, faded }) => {
  const sourceId = `${fileName}-raster_source`;
  const layerId = `${fileName}-raster_layer`;

  return (
    <Source
      id={sourceId}
      type="raster"
      scheme={rasterTileJson[fileName].isTms ? 'tms' : 'xyz'}
      tileSize={256}
      bounds={rasterTileJson[fileName].bounds}
      tiles={[rasterTileJson[fileName].tileUrl]}
    >
      <Layer
        beforeId="footprintStub"
        type="raster"
        id={layerId}
        source={sourceId}
        layout={{
          visibility: visible ? 'visible' : 'none',
        }}
        paint={{
          'raster-opacity': faded ? FADED_RASTER_OPACITY : 1,
        }}
      />
    </Source>
  );
};

interface ComponentProps {
  showLayers: boolean;
}

const MapRasterLayer: React.FC<ComponentProps> = ({ showLayers, rasterTileJson, orgId, projectId, visible, tifFileNames, hiddenRasterStems, fadedRasterStems }) => {
  let rasterReady = 0;
  // Check if every file has corresponding bounds
  const fileNames = tifFileNames?.map((tifName) => {
    const fileName = tifName.split('/').pop();
    const tifFileName = fileName.substring(0, fileName.lastIndexOf('.'));
    if (rasterTileJson && rasterTileJson[tifFileName] && rasterTileJson[tifFileName].bounds) {
      rasterReady += 1;
    }
    return tifFileName;
  });

  return (rasterReady === fileNames?.length) && fileNames?.map((fileName: string) => (
    <RasterLayer
      key={fileName}
      fileName={fileName}
      rasterTileJson={rasterTileJson}
      orgId={orgId}
      projectId={projectId}
      visible={visible && !hiddenRasterStems.includes(fileName)}
      faded={fadedRasterStems.includes(fileName)}
    />
  ));
};

const mapStateToProps = (state: IState, ownProps: ComponentProps) => {
  const { project } = state.project;
  const tifFileNames = project?.tifFiles?.map((tifFile) => (tifFile.filename.split('/').pop()));
  // eslint-disable-next-line no-nested-ternary
  const orgId = _ADMIN_ ? state.admin.orgId : ('opsTrainer' in state.auth.resources ? project?.ownerOrganization : state.auth.user?.ownerOrganization);
  return ({
    rasterTileJson: state.map.common.rasterTileJson,
    orgId,
    projectId: state.project.projectId,
    visible: !ownProps.showLayers || state.map.common.layers.orthomosaic,
    rasterLoading: state.map.common.rasterTileJsonLoading,
    changeRaster: state.map.common.changeRaster,
    tifFileNames,
    hiddenRasterStems: state.map.common.hiddenRasterStems,
    // The fade is an extents reaction: on the customer Project Details map
    // extents are off entirely (extentsConfig), so selection must not dim
    // rasters there. The 2D viewer and admin keep it.
    fadedRasterStems: (_ADMIN_ || ownProps.showLayers || PROJECT_DETAILS_EXTENTS_ENABLED)
      ? getFadedRasterStems(state)
      : NO_FADED_STEMS,
  });
};

MapRasterLayer.propTypes = {
  rasterTileJson: PropTypes.any.isRequired,
  orgId: PropTypes.string.isRequired,
  projectId: PropTypes.any.isRequired,
  visible:  PropTypes.any.isRequired,
  tifFileNames: PropTypes.any.isRequired,
};

export default connect(
  mapStateToProps,
  null,
  null,
  {
    areStatePropsEqual: (next, prev) => next.projectId === prev.projectId &&
      next.orgId === prev.orgId &&
      next.rasterLoading === prev.rasterLoading &&
      next.visible === prev.visible &&
      next.changeRaster === prev.changeRaster &&
      // immer/reselect replace these arrays on change, so reference equality is enough
      next.hiddenRasterStems === prev.hiddenRasterStems &&
      next.fadedRasterStems === prev.fadedRasterStems,
  },
)(MapRasterLayer);
